import * as React from "react";
import { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import { Box, IconButton, CircularProgress } from "@mui/material";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import ReplyIcon from "@mui/icons-material/Reply";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

export interface CameraProps {
    OnTakePicture: (image: string) => void;
    Hidden: boolean;
    OnReturn?: () => void;
}

export const Camera = (props: CameraProps) => {
    const [FacingMode, setFacingMode] = useState<"user" | "environment">("environment");
    const [Ready, setReady] = useState<boolean>(false);
    const webcamRef = useRef<Webcam>(null);

    const CapturePhoto = async () => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            const prefix = "data:image/jpeg;base64,";
            const ImageB64 = imageSrc.startsWith(prefix)
                ? imageSrc
                : `${prefix}${imageSrc.split(",")[1]}`;
            props.OnTakePicture(ImageB64);
        }
    };

    const switchCamera = () => {
        setFacingMode(FacingMode === "environment" ? "user" : "environment");
    };

    useEffect(() => {
        setReady(true);
    }, []);

    return (
        <Box
            sx={{
                display: props.Hidden ? "none" : "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%", // Utilisez 100% pour éviter les débordements
                height: "100%", // Assurez-vous d'occuper toute la hauteur
                backgroundColor: "#000",
                overflow: "hidden", // Empêche les débordements
            }}
        >
            {/* Webcam View */}
            <Webcam
                ref={webcamRef}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%", // Utilisez 100% ici aussi
                    height: "100%", // Remplit toute la hauteur
                    objectFit: "cover", // Adapte la caméra pour couvrir tout l'écran
                }}
                audio={false}
                mirrored={FacingMode === "user"}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                    facingMode: FacingMode,
                }}
            />

            {/* Loading Spinner */}
            {!Ready && (
                <CircularProgress
                    sx={{
                        position: "absolute",
                        color: "white",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                    }}
                    size={60}
                    thickness={4}
                />
            )}

            {/* Controls */}
            {Ready && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "20vh", // Remonte les boutons
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-evenly",
                        alignItems: "center",
                        padding: 2,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    }}
                >
                    {/* Return Button */}
                    {props.OnReturn && (
                        <IconButton
                            onClick={props.OnReturn}
                            sx={{
                                color: "white",
                                "&:hover": { color: "gray" },
                            }}
                        >
                            <ReplyIcon />
                        </IconButton>
                    )}

                    {/* Capture Button */}
                    <IconButton
                        onClick={CapturePhoto}
                        sx={{
                            color: "white",
                            "&:hover": { color: "gray" },
                        }}
                    >
                        <CameraAltIcon sx={{ fontSize: "32px" }} />
                    </IconButton>

                    {/* Switch Camera Button */}
                    <IconButton
                        onClick={switchCamera}
                        sx={{
                            color: "white",
                            "&:hover": { color: "gray" },
                        }}
                    >
                        <CameraswitchIcon />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};
